import { makeAutoObservable, runInAction } from "mobx";
import { type NameCheckerClient } from "./nameCheckerClient";
import type { SensitiveWord } from "./nameCheckerTypes";

export type NameCheckerState = "pending" | "success" | "warning" | "error" | "nameSensitive" | "invalidName";

export class NameCheckerStore {
  private _state: NameCheckerState = "pending";
  private _lastCheckedName?: string = undefined;
  private _sensitiveInfo: SensitiveWord | undefined = undefined;
  private _recommendedName: string | undefined = undefined;

  constructor(private readonly _nameCheckerClient: NameCheckerClient) {
    makeAutoObservable(this);
  }

  get state(): NameCheckerState {
    return this._state;
  }

  set state(value: NameCheckerState) {
    this._state = value;
  }

  get sensitiveWord(): SensitiveWord | undefined {
    return this._sensitiveInfo;
  }

  set sensitiveWord(value: SensitiveWord | undefined) {
    this._sensitiveInfo = value;
  }

  get recommendedName(): string | undefined {
    return this._recommendedName;
  }

  getBusinessNameState = (
    isAvailable: boolean,
    isBusinessNameRegistered: boolean,
    nameChecker: Nullable<SensitiveWord>,
    isValidName: boolean,
  ): NameCheckerState => {
    if (!isValidName) {
      return "invalidName";
    }
    if (isBusinessNameRegistered) {
      return "warning";
    }
    if (nameChecker?.word) {
      return "nameSensitive";
    }
    return isAvailable ? "success" : "error";
  };

  checkNameAvailability = async (name: string): Promise<void> => {
    try {
      if (!name || name === this._lastCheckedName) {
        return;
      }

      runInAction(() => {
        this.state = "pending";
      });

      if (!name) {
        return;
      }

      runInAction(() => {
        this._lastCheckedName = name;
      });

      const nameCheckerValue = await this._nameCheckerClient.checkNameAvailability(name);

      runInAction(() => {
        this.state = this.getBusinessNameState(
          nameCheckerValue.isAvailable,
          nameCheckerValue.isBusinessNameRegistered ?? false,
          nameCheckerValue.sensitiveWord,
          nameCheckerValue.isValidName,
        );
        this._recommendedName = nameCheckerValue.recommendedName;
        if (nameCheckerValue.sensitiveWord) {
          this.sensitiveWord = nameCheckerValue.sensitiveWord;
        } else {
          this.sensitiveWord = undefined;
        }
      });
    } catch (error) {
      this.state = "error";
    }
  };
}
